"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import Loader2 from "lucide-react/dist/esm/icons/loader";
import Mail from "lucide-react/dist/esm/icons/mail";
import { supabase } from "@/integrations/supabase/client";
import { reportIfRateLimited, parseRetryAfterSeconds } from "@/lib/rate-limit-telemetry";
import { toast } from "sonner";
import { TurnstileWidget, isTurnstileEnabled } from "@/components/auth/TurnstileWidget";

interface ResendVerificationProps {
  email: string;
}

export function ResendVerification({ email }: ResendVerificationProps) {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [turnstileKey, setTurnstileKey] = useState(0);

  const handleResend = async () => {
    if (!email) return;
    if (isTurnstileEnabled() && !captchaToken) {
      toast.error("Please complete the verification challenge first.");
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
      ...(captchaToken ? { options: { captchaToken } } : {}),
    });
    setLoading(false);

    if (error) {
      const throttled = reportIfRateLimited(error, "auth_email_resend", email);
      setCaptchaToken(null);
      setTurnstileKey((k) => k + 1);
      const retryIn = throttled ? parseRetryAfterSeconds(error) : null;
      toast.error(
        throttled
          ? retryIn
            ? `Too many requests — try again in ${retryIn} seconds.`
            : "Too many requests — wait a minute and try again."
          : "Failed to resend verification email. Please try again later.",
      );
    } else {
      setSent(true);
      toast.success("Verification email sent! Check your inbox.");
    }
  };

  if (sent) {
    return (
      <p className="text-xs text-muted-foreground text-center">
        ✓ Verification email sent to <strong>{email}</strong>
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <TurnstileWidget key={turnstileKey} onToken={setCaptchaToken} className="flex justify-center" />
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full"
        onClick={handleResend}
        disabled={loading || !email || (isTurnstileEnabled() && !captchaToken)}
      >
        {loading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
        ) : (
          <Mail className="h-3.5 w-3.5 mr-1.5" />
        )}
        Resend verification email
      </Button>
    </div>
  );
}
